import React, { useState, useEffect } from 'react'
import {
  FormControl,
  Box,
  Button,
  TextField,
  ListItem,
  MenuItem,
  Select,
} from '@mui/material'
import '../../../styles/pages.css'
import { addUser } from '../../../providers/mutations'
import { ICreateUser } from 'infrastructure/api'
import { roleNameMap } from '../../utility/utils'
import DeleteIcon from '@mui/icons-material/Delete'
import SearchSchools from '../district_components/SearchSchools'
import { useNavigateTo } from 'infrastructure/routing'
import { useUrlSearchParam } from 'shared/hooks'

export default function UserCreate() {
  const [schoolIdParam] = useUrlSearchParam('school_id')
  const schoolId = schoolIdParam ? Number(schoolIdParam) : undefined
  return <UserCreateWithSchool school_id={schoolId || undefined} />
}

export function UserCreateWithSchool({ school_id }: { school_id?: number }) {
  const [userName, setUserName] = useState('')
  const [userEmail, setUserEmail] = useState('')
  const [userRoles, setUserRoles] = useState<string[]>([])
  const [userSchool, setUserSchool] = useState(school_id ?? 0)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [availableRoles, setAvailableRoles] = useState([''])
  const navigate = useNavigateTo()

  useEffect(() => {
    setAvailableRoles(
      Object.keys(roleNameMap).filter(
        (role) => !userRoles.includes(roleNameMap[role]),
      ),
    )
  }, [userRoles])

  const onChangeName = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError('')
    setSuccess('')
    setUserName(e.target.value)
  }
  const onChangeEmail = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError('')
    setSuccess('')
    // to lower case and remove white spaces at both ends
    setUserEmail(e.target.value.toLocaleLowerCase().trim())
  }

  const getRolesForCreate = (): string[] => {
    let roles: string[] = []
    for (let index = 0; index < userRoles.length; index++) {
      roles.push(roleNameMap[userRoles[index]])
    }
    return roles
  }

  const onRoleSelected = (event: any) => {
    if (userRoles.includes(event.target.value)) {
      return
    }
    setUserRoles([...userRoles, event.target.value])
  }

  const removeRole = (role: string) => {
    setUserRoles(
      userRoles.filter(function (userRole) {
        return userRole !== role
      }),
    )
  }

  const emailBasicValidation = (email: string) => {
    return email.includes('@') && email.includes('.')
  }

  const onSubmit = async () => {
    setSuccess('')
    setError('')
    if (userName.length < 2) {
      setError('User name should be at least 2 characters')
    } else if (!emailBasicValidation(userEmail)) {
      setError('User email should be a valid email')
    } else if (userRoles.length === 0) {
      setError('User must have a role')
    } else if (userSchool === 0) {
      setError('User must have a school')
    } else {
      const userCreate: ICreateUser = {
        name: userName,
        email: userEmail,
        roles: getRolesForCreate(),
        school_id: userSchool,
      }

      const resp = await addUser(userCreate)
      if (resp.errors === undefined || resp.errors.length === 0) {
        setSuccess(resp.data.addUser.message)
        navigate(`/users/${Number(resp.data.addUser.Value)}/show`)
      } else {
        setError(resp.errors[0].message)
      }
    }
  }

  const onSubmitSchool = async (schoolID: number) => {
    setUserSchool(schoolID)
  }

  const removeSchool = () => {
    setUserSchool(-1)
  }

  const displayUserSchool = () => {
    if (userSchool !== -1) {
      return userSchool
    }
    return ''
  }

  return (
    <FormControl style={{ width: '100%' }}>
      <Box component="form" onSubmit={onSubmit}>
        <Box m={2}>
          <TextField
            label="User Name"
            placeholder="User Name"
            variant="outlined"
            onChange={onChangeName}
            value={userName}
            fullWidth
            inputProps={{
              'aria-label': 'User Name',
            }}
          />
        </Box>
        <Box m={2}>
          <TextField
            label="User Email"
            placeholder="User Email"
            variant="outlined"
            onChange={onChangeEmail}
            value={userEmail}
            fullWidth
            inputProps={{
              'aria-label': 'User Email',
            }}
          />
        </Box>
        <Box
          m={2}
          minWidth={'50%'}
          display={'flex'}
          flexDirection={'row'}
          style={{ height: '150px' }}
        >
          <Box style={{ float: 'left' }} minWidth={'50%'}>
            <b>Available User Roles</b>
            <ListItem>
              <Select
                labelId="env-select-label"
                id="env-select"
                value={''}
                onChange={onRoleSelected}
                data-testid="role-select"
              >
                {availableRoles.map((e) => (
                  <MenuItem key={e} value={e}>
                    {e}
                  </MenuItem>
                ))}
              </Select>
            </ListItem>
          </Box>
          <Box style={{ float: 'right' }}>
            <b>Currently selected role(s)</b>
            <Box style={{ overflowY: 'auto', maxHeight: '125px' }}>
              {userRoles.map((role) => (
                <li key={role}>
                  {role}
                  <Button
                    onClick={() => removeRole(role)}
                    key={role}
                    color="primary"
                  >
                    <DeleteIcon />
                  </Button>
                </li>
              ))}
            </Box>
          </Box>
        </Box>
        <Box m={2} minWidth={'50%'} display={'flex'} flexDirection={'column'}>
          <Box>
            <b>Selected School: {displayUserSchool()}</b>
            {school_id === undefined && (
              // only show delete icon if school_id wasn't provided
              <Button onClick={removeSchool} key={-1} color="primary">
                <DeleteIcon />
              </Button>
            )}
          </Box>
          {school_id === undefined && (
            // only show search schools if school_id wasn't provided
            <Box style={{ width: '100%' }}>
              <SearchSchools
                submitFn={onSubmitSchool}
                clearAfterSubmit={true}
              />
            </Box>
          )}
        </Box>
        <Box m={2}>
          <Button onClick={onSubmit} variant="outlined" color="secondary">
            Create!
          </Button>
        </Box>
        <Box m={2}>
          {error && (
            <p data-testid="error" className="error">
              {error}
            </p>
          )}
          {success && <p className="success">{success}</p>}
        </Box>
      </Box>
    </FormControl>
  )
}
